import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Col, Form, Row, Tab } from 'react-bootstrap';
import httpStatusCodes, { HttpStatusCodeItem } from 'utils/httpStatusCodes';
import { RequestMatcherItem } from 'components/transactionEditor/RequestMatchersList';
import { ResponseKeyValueItem } from 'components/transactionEditor/ResponseKeyValueList';
import { defaultUseFormInput, UseFormInput } from 'hooks/useFormInput';
import {
  HttpTransaction,
  MsgTransaction,
  Recording,
  Tag,
  Transaction,
  TransactionType,
  TransactionWrapper,
} from 'types/rest/assetCatalogTypes';
import { generateDate } from 'utils/dateUtil';
import { getKeyByValue } from 'utils/miscUtil';
import i18n from 'i18n';
import {
  Credentials,
  GenericDsl,
  HttpKeyValue,
  Matcher,
  MatcherDsl,
  MsgRequestDsl,
  MsgResponseDsl,
  Namespace,
  RequestDsl,
  ResponseDsl,
  StatusCodeCondition,
} from 'types/rest/dslTypes';
import { BodyMatcherItem, XmlNamespaceItem } from 'components/transactionEditor/BodyMatchersList';
import {
  buildTransactionBody,
  convertToRMI,
  getDefaultHttpEmptyDsl,
  transformConditionsToBase64,
} from 'utils/transactionUtils';
import { DelayType, ResponseDelay } from 'types/rest/mockServiceTypes';
import { useDispatch } from 'react-redux';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import ResponseTab, {
  ResponseTabState,
} from 'views/AssetCatalog/Transactions/TransactionEditor/ResponseTab/ResponseTab';
import RequestTab, { RequestTabState } from 'views/AssetCatalog/Transactions/TransactionEditor/RequestTab/RequestTab';
import ProcessingActions from 'views/AssetCatalog/Transactions/TransactionEditor/ProcessingActions/ProcessingActions';
import TransactionCommonHeader from './TransactionCommonHeader';
import TransactionVersionHistoryTab from './History/TransactionVersionHistoryTab';
import { Dispatch } from 'redux';
import { MessagingRequestTabState } from './RequestTab/MessagingRequestTab';
import Button from 'react-bootstrap/Button';
import Nav from 'react-bootstrap/Nav';
import { useIsMount } from '../../../../hooks/useIsMountHook';
import _ from 'lodash';
import BzmIcon from '../../../../components/icons/BzmIcon';
import { format } from 'sql-formatter';

export interface TransactionEditorProps {
  transaction: Transaction;
  serviceId?: number;
  onSave: (transaction: TransactionWrapper, isCreate: boolean, selectedEditorTab?: EditorTabType) => void;
  isCreate: boolean;
  isReadOnly?: boolean;
  isResponseBodyMagicStringInvalid?: boolean;
  isResponseHeaderMagicStringInvalid?: boolean;
  resHeaderInvalidMagicStrings?: string;
  resBodyInvalidMagicStrings?: string;
  /** If it is embedded in modal or in some other wrapper*/
  isEmbedded?: boolean;
  isWizardModal?: boolean;
  /** If component is shown in archived view, the created label is different. */
  isArchivedView?: boolean;
  setInvalidTransaction?: () => void;
  /** Initial tab to be open on mount */
  selectedEditorTab?: EditorTabType;
  /** When Tab Changes */
  onTabChanged?: (tab: EditorTabType) => void;
  insideSandbox?: boolean; // Show header with save button and service selector
  updateTransactionFromSandbox?: () => void;
}

export type TransactionField = 'name' | 'description' | 'tags';
export type RecordingField = TransactionField; //they have fields with same names

/**
 * Provides default values to be passed to TransactionEditor when creating a new Transaction
 */
export class EmptyTransaction implements Transaction {
  type: TransactionType;
  description = '';
  serviceId = 0;
  dsl: GenericDsl = null;
  id = -1;
  name = `${i18n.t('transactions:table.txn')}-` + generateDate();
  tags = [];
  priority = 10;

  constructor(
    txType: TransactionType,
    serviceId: number,
    name?: string,
    matchingValue?: string,
    txId?: number,
    content?: string,
  ) {
    this.type = txType;
    this.serviceId = serviceId;

    if (txId !== undefined) {
      this.id = txId;
    }
    if (name !== undefined) {
      this.name = name;
    }

    if (txType === 'HTTP') {
      this.dsl = {
        type: 'HTTP',
        requestDsl: {
          method: 'GET',
          host: '',
          path: '',
          url: {
            key: 'url',
            matcherName: Matcher.equals_url,
            matchingValue: matchingValue !== undefined ? matchingValue : '',
          },
        } as RequestDsl,
        responseDsl: {
          status: 200,
          statusMessage: '',
          proxyUrl: '',
          contentType: 'json',
          content: content !== undefined ? content : '',
          binary: false,
          responseDelay: { type: DelayType.NONE } as ResponseDelay,
        } as ResponseDsl,
      };
    } else {
      this.dsl = {
        type: 'MESSAGING',
        requestDsl: {
          headers: [],
          properties: [],
          body: [],
        } as MsgRequestDsl,
        responseDsl: {
          messageType: 'TEXT_MESSAGE',
          content: content !== undefined ? content : '',
          charset: 'UTF-8',
          responseDelay: { type: DelayType.NONE } as ResponseDelay,
          failoverEnabled: false,
        } as MsgResponseDsl,
      };
    }
  }
}

export type MatcherKeys = { [key in Matcher]?: string };

export const transactionMatchersDropdownOperators: MatcherKeys = {
  equals: i18n.t('transactions:editor.equals'),
  equals_insensitive: i18n.t('transactions:editor.equalsCaseInsensitive'),
  contains: i18n.t('transactions:editor.containsText'),
  matches: i18n.t('transactions:editor.matchesRegex'),
  not_matches: i18n.t('transactions:editor.doesNotMatchRegex'),
  absent: i18n.t('transactions:editor.isAbsent'),
};
export const bodyMatchersDropdownOperators: MatcherKeys = {
  equals_xml: i18n.t('transactions:editor.equalsToXml'),
  equals_json: i18n.t('transactions:editor.equalsToJson'),
  matches_xml: i18n.t('transactions:editor.matchesXpath'),
  matches_xml_cdata: i18n.t('transactions:editor.matchesCdataXpath'),
  matches_json: i18n.t('transactions:editor.matchesJsonPath'),
  matches_xml_schema: i18n.t('transactions:editor.matchesWsdlSchema'),
  matches_graphql_body: i18n.t('transactions:editor.matchesGraphqlBody'),
  matches_graphql_schema: i18n.t('transactions:editor.matchesGraphqlSchema'),
  not_matches_graphql_schema: i18n.t('transactions:editor.notMatchesGraphqlSchema'),
};

export const conditionalOperators: MatcherKeys = {
  greater_than: i18n.t('transactions:editor.greaterThan'),
  less_than: i18n.t('transactions:editor.lessThan'),
};

export const allMatcherOperators: MatcherKeys = {
  ...transactionMatchersDropdownOperators,
  ...bodyMatchersDropdownOperators,
  ...conditionalOperators,
};

export const wizardMatchersDropdownOperators: MatcherKeys = {
  equals: i18n.t('transactions:editor.equals'),
};

export type EditorTabType = 'request' | 'response' | 'processingActions' | 'redirectToLive' | 'history';

export const createTransactionState = <T extends Transaction>(
  transaction: Transaction,
  getDefaultDsl: () => any,
): T => {
  if (_.isNil(transaction.dsl)) {
    const txnWithBlankDsl = _.cloneDeep(transaction);
    txnWithBlankDsl.dsl = getDefaultDsl();
    return txnWithBlankDsl as T;
  }
  return transaction as T;
};

// Usage examples
const createHttpTransactionState = (transaction: Transaction): HttpTransaction => {
  return createTransactionState<HttpTransaction>(transaction, getDefaultHttpEmptyDsl);
};

const convertToMatcherDsl = (item: RequestMatcherItem): MatcherDsl | null => {
  // skip all the items with empty name
  if (!item.name || item.name === '') {
    return null;
  } else {
    const operatorValue: Matcher = getKeyByValue(transactionMatchersDropdownOperators, item.operator) as Matcher;
    const isOptional = !_.isNil(item.isRequired) ? !item.isRequired : false;
    return {
      key: item.name as string,
      optional: isOptional,
      matcherName: operatorValue ? operatorValue : (item.operator as Matcher),
      matchingValue: operatorValue && operatorValue === 'absent' ? '' : item.value ? (item.value as string) : '',
    };
  }
};
export const convertToMatcherDsls = (items: RequestMatcherItem[]): MatcherDsl[] => {
  const matchers = items.map(i => convertToMatcherDsl(i));
  return matchers.filter(m => m !== null) as MatcherDsl[];
};

export const convertToRMIs = (idPrefix: string, matchers?: MatcherDsl[]): RequestMatcherItem[] => {
  if (!matchers) {
    return [];
  }
  return matchers.map((m, ix) => convertToRMI(m, idPrefix + '-' + ix));
};

const buildNameSpaces = (namespaces: Namespace[]): XmlNamespaceItem[] => {
  return namespaces.map((nm, idx) => ({
    id: 'xml-namespace-list-' + idx,
    prefix: nm.prefix ? Buffer.from(nm.prefix, 'base64').toString() : '',
    uri: nm.uri ? Buffer.from(nm.uri, 'base64').toString() : '',
  }));
};
/**
 * Converts backend Matcher representation to UI one.
 * @param body
 */
export const convertToBMIs = (body: MatcherDsl[]): BodyMatcherItem[] => {
  return body.map((bm, ix) => ({
    id: 'body-matchers-' + ix,
    matcher: bm.matcherName,
    cdataXpath: bm.cdataXpath ? Buffer.from(bm.cdataXpath, 'base64').toString() : null,
    content: bm.matchingValue ? Buffer.from(bm.matchingValue, 'base64').toString() : '',
    sampleBody: bm.sampleBody != null ? Buffer.from(bm.sampleBody, 'base64').toString() : null,
    isRequired: !_.isNil(bm.optional) ? !bm.optional : true,
    namespaces: bm.namespaces ? buildNameSpaces(bm.namespaces) : [],
  }));
};

export const convertToHI = (matchers?: HttpKeyValue[]): ResponseKeyValueItem[] => {
  if (!matchers) {
    return [];
  }
  return matchers.map((m, ix) => ({
    id: 'header-item-' + ix,
    name: m.name,
    value: m.value,
    type: m.type,
  }));
};

/** Checks if transaction is in valid state */
export const isTxnValid = (
  requestTabState: any,
  responseTabState: any,
  messagingName: UseFormInput,
  serviceId: number,
) => {
  return requestTabState.valid && responseTabState.isValid && messagingName.valid && serviceId && serviceId !== 0;
};

export const changeTab = (
  selectedTab: EditorTabType,
  setTabRRState: (selectedTab: EditorTabType) => void,
  onTabChanged?: (tab: EditorTabType) => void,
) => {
  onTabChanged ? onTabChanged(selectedTab) : _.noop();
  setTabRRState(selectedTab);
};

export const updateTransactionState = _.debounce(
  (
    field: TransactionField,
    value: any,
    setDirty: (dirty: boolean) => void,
    transactionState: any,
    setTransactionState: (transaction: any) => void,
  ) => {
    const transaction = _.cloneDeep(transactionState);
    _.set(transaction, field, value);
    setDirty(true);
    setTransactionState(transaction);
  },
  300,
);

export const handleTransactionChange = (
  e: any,
  field: TransactionField,
  setDirty: (dirty: boolean) => void,
  transactionState: any,
  setTransactionState: (transaction: any) => void,
) => {
  const target = e.target as HTMLInputElement;
  updateTransactionState(field, target.value, setDirty, transactionState, setTransactionState);
};

const isRequestTabState = (state: RequestTabState | MessagingRequestTabState): state is RequestTabState => {
  return (state as RequestTabState).url !== undefined;
};

export const initialValidations = (
  setValidating: (value: ((prevState: boolean) => boolean) | boolean) => void,
  transactionName: UseFormInput,
  requestTabState: RequestTabState | MessagingRequestTabState,
  setTabRRState: (value: EditorTabType) => void,
  onTabChanged: (tab: EditorTabType) => void,
  isValidResponseTabState: boolean,
  isWizardModal: boolean,
  dispatch?: Dispatch<any>,
) => {
  setValidating(true);
  transactionName.validate();
  if (isRequestTabState(requestTabState)) {
    if ('matchingValue' in requestTabState.url) {
      requestTabState.validate && requestTabState.validate(requestTabState.url.matchingValue);
    }
  } else {
    //otherwise we are using requestTabState as MessagingRequestTabState
    if (
      ![requestTabState.headers.length, requestTabState.properties.length, requestTabState.body.length].some(
        length => length > 0,
      )
    ) {
      dispatch(showToastNotification(ToastType.Danger, i18n.t('transactions:actions.transactionMessageDefineMatcher')));
    }
  }

  // make sure request tab is shown when there is a validation error
  if (!requestTabState.valid) {
    changeTab('request', setTabRRState, onTabChanged);
  }

  if (!isValidResponseTabState) {
    changeTab('response', setTabRRState, onTabChanged);
  }
};

export const saveValidatedTransaction = (
  transaction: HttpTransaction | MsgTransaction,
  serviceId: number,
  selectedTab: EditorTabType,
  setTabRRState: (value: EditorTabType) => void,
  onSave: (transaction: TransactionWrapper, isCreate: boolean, selectedEditorTab?: EditorTabType) => void,
  isCreate: boolean,
) => {
  const transactionWrapper: TransactionWrapper = {
    transaction: transaction,
    serviceId: serviceId,
  };

  //Change tab if needed
  if (selectedTab) {
    setTabRRState(selectedTab);
  }

  //Propagate change up
  onSave(transactionWrapper, isCreate, selectedTab);
};

/**
 * Checks if txn is persisted by backend.
 */
const isTxnSaved = (transactionId: number) => {
  return transactionId !== -1;
};

export const onTabChanged = (
  tabKey: EditorTabType,
  setIsSaveModalShown: (value: boolean) => void,
  setTabRRState: (edit: EditorTabType) => void,
  onTabChanged: (tab: EditorTabType) => void,
  transactionId: number,
) => {
  //If txn is not saved yet, show modal with prompt
  if (tabKey === 'processingActions' && !isTxnSaved(transactionId)) {
    setIsSaveModalShown(true);
  } else {
    changeTab(tabKey, setTabRRState, onTabChanged);
  }
};

export const tagSelectHandler = <T extends HttpTransaction | MsgTransaction | Recording>(
  tags: Tag[],
  setDirty: (dirty: boolean) => void,
  transactionState: T,
  setTransactionState: (transaction: T) => void,
) => {
  updateTransactionState(
    'tags',
    tags.map(t => t.name),
    setDirty,
    transactionState,
    setTransactionState,
  );
};

export const editorComponentsState = <T extends HttpTransaction | MsgTransaction>(
  props: TransactionEditorProps,
  createTransactionState: (transaction: Transaction) => T,
) => {
  // MOB-47723: 'history' tab exists for HTTP only; fall back to 'request' otherwise so messaging never opens blank
  const initialTab: EditorTabType =
    props.selectedEditorTab && !(props.selectedEditorTab === 'history' && props.transaction?.type !== 'HTTP')
      ? props.selectedEditorTab
      : 'request';
  const [tabRRState, setTabRRState] = useState<EditorTabType>(initialTab);
  const [transactionState, setTransactionState] = useState<T>(createTransactionState(props.transaction));
  const [serviceId, setServiceId] = useState(props.serviceId as number);
  const [serviceName, setServiceName] = useState(props.transaction ? props.transaction.serviceName : '');
  const [isDirty, setDirty] = useState(!serviceId); // indicates whether a change was made in the editor to enable Save button
  const [isValidating, setValidating] = useState(false);
  const [selectedResponseTab, setSelectedResponseTab] = useState('body');
  const [isSaveModalShown, setIsSaveModalShown] = useState(false);
  const [transactionName, setTransactionName] = useState<UseFormInput>(defaultUseFormInput);
  return {
    tabRRState,
    setTabRRState,
    transactionState,
    setTransactionState,
    serviceId,
    setServiceId,
    serviceName,
    setServiceName,
    isDirty,
    setDirty,
    isValidating,
    setValidating,
    selectedResponseTab,
    setSelectedResponseTab,
    isSaveModalShown,
    setIsSaveModalShown,
    transactionName,
    setTransactionName,
  };
};

// eslint-disable-next-line react/display-name
const TransactionEditor = forwardRef((props: TransactionEditorProps, ref) => {
  const {
    tabRRState,
    setTabRRState,
    transactionState,
    setTransactionState,
    serviceId,
    setServiceId,
    serviceName,
    setServiceName,
    isDirty,
    setDirty,
    isValidating,
    setValidating,
    selectedResponseTab,
    setSelectedResponseTab,
    isSaveModalShown,
    setIsSaveModalShown,
    transactionName,
    setTransactionName,
  } = editorComponentsState(props, createHttpTransactionState);

  // The component instance will be extended
  // with whatever you return from the callback passed
  // as the second argument
  useImperativeHandle(ref, () => ({
    saveTransactionHandler() {
      saveTransaction();
    },
  }));
  const [isInitializingSandbox, setIsInitializingSandbox] = useState(true);

  const dispatch = useDispatch();
  const saveTransaction = (selectedTab?: EditorTabType) => {
    initialValidations(
      setValidating,
      transactionName,
      requestTabState,
      setTabRRState,
      props.onTabChanged,
      responseTabState.isValid,
      props.isWizardModal,
      dispatch,
    );

    const validDefinition = isTxnValid(requestTabState, responseTabState, transactionName, serviceId);

    //Save after validations
    if (isTxnValid(requestTabState, responseTabState, transactionName, serviceId) || props.isWizardModal === true) {
      const requestDsl: RequestDsl = {
        method: requestTabState.method,
        host: requestTabState.url.matchingValue,
        path: requestTabState.path,
        url: {
          key: requestTabState.url.key,
          matcherName: requestTabState.url.matcherName,
          matchingValue: requestTabState.url.matchingValue,
        },
        headers: convertToMatcherDsls(requestTabState.headers),
        queryParams: convertToMatcherDsls(requestTabState.queryParams),
        cookies: convertToMatcherDsls(requestTabState.cookies),
        credentials:
          requestTabState.credentials && (requestTabState.credentials.username || requestTabState.credentials.password)
            ? requestTabState.credentials
            : undefined,
        body: buildTransactionBody(requestTabState.body),
      };

      const responseDsl: ResponseDsl = {
        status: responseTabState.status.statusCode,
        statusMessage: responseTabState.status.statusMsg,
        proxyUrl: responseTabState.proxyUrl,
        headers: responseTabState.headers.map(item => ({
          name: item.name || '',
          value: item.value || '',
        })),
        contentType: responseTabState.contentType,
        content: responseTabState.binary
          ? responseTabState.content.toString()
          : Buffer.from(responseTabState.content).toString('base64'),
        binary: responseTabState.binary,
        responseDelay: responseTabState.responseDelay,
        failoverEnabled: responseTabState.failoverEnabled,
        statusCodeConditions: transformConditionsToBase64(
          responseTabState.statusCodeConditions,
          'base64encode',
        ) as StatusCodeCondition[],
        fault: responseTabState.fault,
      };

      const transaction: HttpTransaction = {
        type: transactionState.type,
        id: transactionState.id,
        name: transactionName.value as string,
        dsl: {
          type: transactionState.type,
          redirectUrl: responseTabState.redirectUrl,
          certificateId: responseTabState.certificateId,
          priority: 1,
          requestDsl: requestDsl,
          responseDsl: responseDsl,
        },
        description: transactionState.description,
        serviceName: serviceName,
        serviceId: serviceId,
        tags: transactionState.tags,
        priority: transactionState.priority,
        assets: responseTabState.assets,
        configKeyAssets: responseTabState.configKeyAssets,
        sqlHint: transactionState.sqlHint,
      };
      saveValidatedTransaction(transaction, serviceId, selectedTab, setTabRRState, props.onSave, props.isCreate);

      if (props.isWizardModal === true) {
        return { validDefinition: validDefinition };
      }
    }
  };

  const buildInitRequestTabState = (txRequestDsl?: RequestDsl): RequestTabState => {
    const requestDsl = txRequestDsl ? txRequestDsl : transactionState.dsl.requestDsl;
    return {
      validate: null,
      valid: !!requestDsl.url.matchingValue,
      dirty: false,
      method: requestDsl.method,
      host: requestDsl.host,
      path: requestDsl.path,
      url: {
        key: requestDsl.url.key,
        matcherName: requestDsl.url.matcherName,
        matchingValue: requestDsl.url.matchingValue,
      },
      headers: convertToRMIs('matchers-header', requestDsl.headers),
      queryParams: convertToRMIs('matchers-query-params', requestDsl.queryParams),
      cookies: convertToRMIs('matchers-cookies', requestDsl.cookies),
      credentials: _.isEmpty(requestDsl.credentials) ? null : (requestDsl.credentials as Credentials),
      body: requestDsl.body ? convertToBMIs(requestDsl.body) : [],
      legacySampleBody: (transactionState.sampleBody as string) ?? null,
    };
  };

  const initRequest = buildInitRequestTabState();
  const [requestTabState, setRequestTabState] = useState(initRequest);
  const requestInitialState = _.cloneDeep(initRequest);

  const buildInitResponseTabState = (txResponseDsl?: ResponseDsl): ResponseTabState => {
    const responseDsl = txResponseDsl ? txResponseDsl : { ...transactionState.dsl.responseDsl };
    const httpStatus = httpStatusCodes.find(code => code.statusCode === responseDsl.status) as HttpStatusCodeItem;
    return {
      status: httpStatus,
      proxyUrl: responseDsl.proxyUrl,
      headers: convertToHI(responseDsl.headers),
      contentType: responseDsl.contentType,
      content: responseDsl.binary
        ? responseDsl.content
          ? responseDsl.content
          : ''
        : responseDsl.content
        ? Buffer.from(responseDsl.content, 'base64').toString()
        : '',
      binary: responseDsl.binary,
      dirty: false,
      statusCodeConditions: transformConditionsToBase64(
        responseDsl.statusCodeConditions,
        'base64decode',
      ) as StatusCodeCondition[],
      redirectUrl: transactionState.dsl.redirectUrl ? transactionState.dsl.redirectUrl : undefined,
      assets: transactionState.assets,
      configKeyAssets: transactionState.configKeyAssets,
      responseDelay: responseDsl.responseDelay
        ? responseDsl.responseDelay
        : ({ type: DelayType.NONE } as ResponseDelay),
      isValid: true,
      failoverEnabled: responseDsl.failoverEnabled,
      fault: responseDsl.fault,
    };
  };

  const initResponse = buildInitResponseTabState();
  const [responseTabState, setResponseTabState] = useState(buildInitResponseTabState());
  const responseInitialState = _.cloneDeep(initResponse);

  const isMount = useIsMount();

  useEffect(() => {
    if (!isDirty) {
      setDirty(transactionName.dirty || requestTabState.dirty || responseTabState.dirty);
    }
    if (transactionState.updated !== props.transaction.updated) {
      setTransactionState(props.transaction as HttpTransaction);
      setRequestTabState(buildInitRequestTabState((props.transaction as HttpTransaction).dsl.requestDsl));
      setResponseTabState(buildInitResponseTabState((props.transaction as HttpTransaction).dsl.responseDsl));
    }
  }, [isDirty, transactionName.dirty, requestTabState.dirty, responseTabState.dirty, props.transaction]);

  const compareRequestStateIgnoreFields = () =>
    _.isEqualWith(requestTabState, requestInitialState, (objValue, othValue, key) => {
      if (key === 'validate' || key === 'dirty') {
        return true; // Ignore this fields by treating them as equal
      }
    });

  useEffect(() => {
    if (props.isWizardModal === true && props.setInvalidTransaction !== undefined) {
      const result = saveTransaction();
      if (result?.validDefinition === false) {
        props.setInvalidTransaction();
      }
    } else {
      if (!isMount && props.insideSandbox === true) {
        const shouldSaveTransaction =
          !isInitializingSandbox ||
          !(compareRequestStateIgnoreFields() && _.isEqual(responseTabState, responseInitialState));

        if (shouldSaveTransaction) {
          if (isInitializingSandbox === true) {
            setIsInitializingSandbox(false);
          }
          saveTransaction();
        }
      }
    }
  }, [transactionState, requestTabState, responseTabState]);

  const sqlHintChanged = (hint: string) => {
    const tempTx = _.cloneDeep(transactionState);
    tempTx.sqlHint = btoa(hint);
    setTransactionState(tempTx);
  };

  const handleFormatSql = () => {
    try {
      const formattedSql = format(atob(transactionState.sqlHint), { language: 'sql' }); // Formats the SQL script
      sqlHintChanged(formattedSql);
    } catch (error) {
      console.error('Error formatting SQL:', error);
    }
  };

  // helper to style each tab link
  const linkStyle = (key: string) => ({
    border: 'none',
    ...(tabRRState === key
      ? {
          borderBottom: '3px solid #5405ff', // purple underline
          marginBottom: '-1px', // sit on top of grey line
        }
      : {}),
  });
  const navStyle = props.insideSandbox === true ? 'w-100 mt-3' : 'w-100 ml-0';
  // MOB-47723: show version History tab only for a saved, editable HTTP transaction (not in the read-only version preview)
  const showHistoryTab =
    props.insideSandbox !== true &&
    !props.isReadOnly &&
    !props.isArchivedView &&
    !props.isCreate &&
    !!props.transaction &&
    props.transaction.id !== -1 &&
    props.transaction.type === 'HTTP';
  return (
    <Form
      id="qa-transaction-editor"
      className={props.isEmbedded ? 'bg-white' : props.insideSandbox === true ? 'm-0' : 'm-4'}
    >
      {props.insideSandbox !== true && (
        <TransactionCommonHeader
          setIsSaveModalShown={setIsSaveModalShown}
          saveTransaction={saveTransaction}
          saveModalShown={isSaveModalShown}
          editorProps={props}
          serviceId={serviceId}
          setServiceId={setServiceId}
          setServiceName={setServiceName}
          setDirty={setDirty}
          validating={isValidating}
          transactionState={transactionState}
          tagSelectHandler={(tags: Tag[]) => {
            tagSelectHandler<HttpTransaction>(tags, setDirty, transactionState, setTransactionState);
          }}
          onChange={event =>
            handleTransactionChange(event, 'description', setDirty, transactionState, setTransactionState)
          }
          updateTransactionState={(field: TransactionField, value: any) =>
            updateTransactionState(field, value, setDirty, transactionState, setTransactionState)
          }
          updateNameInput={setTransactionName}
        />
      )}

      <Tab.Container
        activeKey={tabRRState}
        onSelect={(key: any) =>
          onTabChanged(key, setIsSaveModalShown, setTabRRState, props.onTabChanged, props.transaction.id)
        }
      >
        <div className="d-flex align-items-center mb-3">
          <Nav variant="tabs" className={navStyle} style={{ borderBottom: '1px solid #aaaab9' }}>
            <Nav.Item>
              <Nav.Link eventKey="request" style={linkStyle('request')}>
                {i18n.t('transactions:editor.requestMatcher')}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="response" style={linkStyle('response')}>
                {i18n.t('transactions:editor.response')}
              </Nav.Link>
            </Nav.Item>
            {props.insideSandbox !== true && (
              <Nav.Item>
                <Nav.Link eventKey="processingActions" style={linkStyle('processingActions')}>
                  {i18n.t('transactions:processingActions.title')}
                </Nav.Link>
              </Nav.Item>
            )}
            {props.insideSandbox !== true && (
              <Nav.Item>
                <Nav.Link eventKey="sqlHint" style={linkStyle('sqlHint')}>
                  {i18n.t('mockServices:mockServiceDetails.sqlHint')}
                </Nav.Link>
              </Nav.Item>
            )}
            {showHistoryTab && (
              <Nav.Item>
                <Nav.Link eventKey="history" style={linkStyle('history')}>
                  {i18n.t('transactions:versionHistory.tab')}
                </Nav.Link>
              </Nav.Item>
            )}
          </Nav>
          {/* pushes itself to the far right */}
          {props.insideSandbox === true && (
            <Button
              className={'mr-4 ml-5 mb-3'}
              onClick={() => props.updateTransactionFromSandbox()}
              id="qa-btn-save-transaction"
              title={i18n.t('transactions:editor.saveTxn')}
              style={{ width: 170 }}
            >
              {i18n.t('common:actions.applyToTransaction')}
            </Button>
          )}
        </div>

        <Tab.Content>
          <Tab.Pane
            id="qa-tab-request"
            title={i18n.t('transactions:editor.requestMatcher')}
            style={{ marginTop: '-24px' }}
            eventKey="request"
          >
            <RequestTab
              isReadOnly={props.isReadOnly}
              state={requestTabState}
              updateState={setRequestTabState}
              isWizardModal={props.isWizardModal}
              insideSandBox={props.insideSandbox}
              type={props.transaction.type}
              transactionId={props.transaction.id}
            />
          </Tab.Pane>
          <Tab.Pane
            id="qa-tab-response"
            title={i18n.t('transactions:editor.response')}
            style={{ marginTop: '-24px' }}
            eventKey="response"
          >
            <ResponseTab
              isReadOnly={props.isReadOnly}
              state={responseTabState}
              isResponseBodyMagicStringInvalid={props.isResponseBodyMagicStringInvalid}
              isResponseHeaderMagicStringInvalid={props.isResponseHeaderMagicStringInvalid}
              updateState={setResponseTabState}
              requestTabState={requestTabState}
              resHeaderInvalidMagicStrings={props.resHeaderInvalidMagicStrings}
              resBodyInvalidMagicStrings={props.resBodyInvalidMagicStrings}
              activeResponseTab={selectedResponseTab}
              activeResponseTabChanged={activeTab => {
                setSelectedResponseTab(activeTab);
              }}
              isWizardModal={props.isWizardModal}
              serviceId={props.transaction.serviceId}
              insideSandBox={props.insideSandbox}
              type={props.transaction.type}
              transactionId={props.transaction.id}
            />
          </Tab.Pane>
          {props.insideSandbox !== true && (
            <Tab.Pane
              id="qa-tab-processing-actions"
              title={i18n.t('transactions:processingActions.title')}
              style={{ marginTop: '-24px' }}
              eventKey="processingActions"
            >
              <ProcessingActions
                transactionId={props.transaction.id ? props.transaction.id : 1}
                serviceId={serviceId}
                tableOptions={{
                  isSelectable: true,
                  isSimple: false,
                  columns: {
                    name: true,
                    type: true,
                    conditions: true,
                    actions: true,
                  },
                }}
              />
            </Tab.Pane>
          )}
          {props.insideSandbox !== true && (
            <Tab.Pane
              id="qa-tab-sql"
              title={i18n.t('mockServices:mockServiceDetails.sqlHint')}
              style={{ marginTop: '-24px' }}
              eventKey="sqlHint"
            >
              <div className={'bg-light p-2'}>
                <h4 className={'ml-4 mt-4'}>{i18n.t('transactions:editor.sqlStatement')}</h4>
                <p className={'ml-4 mb-0'}>{i18n.t('transactions:editor.sqlHintDescription')}</p>
                <Col md={6}>
                  {/* Format */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '2px' }}>
                    <Button
                      onClick={handleFormatSql}
                      variant={'secondary'}
                      size={'sm'}
                      disabled={_.isEmpty(transactionState.sqlHint)}
                      title={i18n.t('transactions:editor.formatSQL')}
                    >
                      <BzmIcon variant={'icon-bzm-timeline'} className={'mr-3 icon-xs align-middle'} />
                      <span className={'align-middle'}>{i18n.t('transactions:editor.formatSQL')}</span>
                    </Button>
                  </div>
                  {/* ./Format */}
                  <Form.Control
                    as="textarea"
                    disabled={false}
                    id="qa-textarea-body-content"
                    rows={6}
                    name="content"
                    placeholder={i18n.t('transactions:editor.sqlHintContentPlaceHolder')}
                    onChange={e => sqlHintChanged(e.target.value)}
                    value={transactionState.sqlHint ? atob(transactionState.sqlHint) : ''}
                    isInvalid={false}
                  />
                </Col>
                <Col md={6}></Col>
              </div>
            </Tab.Pane>
          )}
          {showHistoryTab && (
            <Tab.Pane
              id="qa-tab-history"
              title={i18n.t('transactions:versionHistory.tab')}
              style={{ marginTop: '-24px' }}
              eventKey="history"
            >
              {tabRRState === 'history' && (
                <TransactionVersionHistoryTab
                  transaction={props.transaction}
                  renderVersionEditor={versionTransaction => (
                    <TransactionEditor
                      transaction={versionTransaction}
                      serviceId={versionTransaction.serviceId}
                      onSave={_.noop}
                      isCreate={false}
                      isReadOnly={true}
                      isArchivedView={true}
                    />
                  )}
                />
              )}
            </Tab.Pane>
          )}
        </Tab.Content>
      </Tab.Container>
    </Form>
  );
});

export default TransactionEditor;
